// Culture variants of the settlement ring (docs/02-systems/building-styles.md,
// stage 4): the houses and keep around a city centre follow the owner's player
// type instead of a per-hex timber/stone/brick roll, so the whole city reads as
// one culture. Same instancing contract as every other form: one geometry pair
// per form, per-instance colours. Footprints match the shared cottage /
// longhouse / townhouse, so placement and spacing are unchanged.

import * as THREE from 'three';
import { bakeShade, merge } from './TerrainScatterMaterials.js';
import { type BuildingForm, FORM_PARTS, type Part, gableRoof } from './settlementForms.js';

export type CultureId = 'imperial' | 'nordic' | 'eastern';
export const CULTURE_IDS: readonly CultureId[] = ['imperial', 'nordic', 'eastern'];
export type HouseBase = 'cottage' | 'longhouse' | 'townhouse' | 'keep';
export const HOUSE_BASES: readonly HouseBase[] = ['cottage', 'longhouse', 'townhouse', 'keep'];
export type CultureForm = `${HouseBase}:${CultureId}`;

const WALL = { bottom: 0.72, top: 1.02, jitter: 0.1 };
const ROOF = { bottom: 0.78, top: 1.08, jitter: 0.16 };

/** Footprint (w, d), wall height, roof height of the shared houses. */
const HOUSES: Record<Exclude<HouseBase, 'keep'>, readonly [number, number, number, number]> = {
  cottage: [0.26, 0.2, 0.15, 0.12],
  longhouse: [0.38, 0.2, 0.15, 0.12],
  townhouse: [0.21, 0.2, 0.28, 0.13],
};

/** Square frustum roof (4-sided), eaves overhanging a w x d footprint. */
function frustum(w: number, d: number, y: number, h: number, topScale: number, over: number) {
  const side = w * over;
  const g = new THREE.CylinderGeometry((side / Math.SQRT2) * topScale, side / Math.SQRT2, h, 4)
    .rotateY(Math.PI / 4)
    .scale(1, 1, (d * over) / side)
    .translate(0, y + h / 2, 0);
  return g;
}

type RoofMaker = (
  w: number,
  d: number,
  wallH: number,
  roofH: number,
  seed: number,
) => THREE.BufferGeometry;

const ROOF_MAKERS: Record<CultureId, RoofMaker> = {
  // Low hip roof: a shallow frustum.
  imperial: (w, d, wallH, roofH, seed) =>
    bakeShade(frustum(w, d, wallH, roofH * 0.75, 0.25, 1.2), { ...ROOF, seed }),
  // Steep gable with a crossed finial pair on the ridge ends.
  nordic: (w, d, wallH, roofH, seed) => {
    const roof = bakeShade(gableRoof(w * 1.1, d * 1.18, roofH * 1.9).translate(0, wallH, 0), {
      ...ROOF,
      seed,
    });
    const tips = [-1, 1].map((s) =>
      bakeShade(
        new THREE.ConeGeometry(0.012, 0.06, 4).translate(
          s * w * 0.55,
          wallH + roofH * 1.9 + 0.02,
          0,
        ),
        { bottom: 0.9, top: 1.1, jitter: 0.05, seed: seed + 5 },
      ),
    );
    return merge([roof, ...tips]);
  },
  // Swept eaves: a wide low frustum, a narrower steep one above, tips curling at the corners.
  eastern: (w, d, wallH, roofH, seed) => {
    const lower = bakeShade(frustum(w, d, wallH, roofH * 0.45, 0.62, 1.4), { ...ROOF, seed });
    const upper = bakeShade(frustum(w, d, wallH + roofH * 0.45, roofH * 0.7, 0.12, 0.85), {
      ...ROOF,
      seed: seed + 1,
    });
    const tips = [-1, 1].flatMap((sx) =>
      [-1, 1].map((sz) =>
        bakeShade(
          new THREE.ConeGeometry(0.014, 0.05, 4).translate(
            sx * w * 0.68,
            wallH + roofH * 0.45 + 0.012,
            sz * d * 0.7,
          ),
          { bottom: 0.9, top: 1.1, jitter: 0.05, seed: seed + 2 },
        ),
      ),
    );
    return merge([lower, upper, ...tips]);
  },
};

function culturalHouse(base: Exclude<HouseBase, 'keep'>, culture: CultureId, seed: number) {
  const [w, d, h, roofH] = HOUSES[base];
  return {
    walls: bakeShade(new THREE.BoxGeometry(w, h, d).translate(0, h / 2, 0), { ...WALL, seed }),
    roof: ROOF_MAKERS[culture](w, d, h, roofH, seed + 1),
  } satisfies Record<Part, THREE.BufferGeometry>;
}

/** Keeps: a domed drum (imperial), a tall stave tower (nordic), a tiered pagoda (eastern). */
const KEEPS: Record<CultureId, () => Record<Part, THREE.BufferGeometry>> = {
  imperial: () => ({
    walls: bakeShade(new THREE.CylinderGeometry(0.17, 0.18, 0.4, 12).translate(0, 0.2, 0), {
      ...WALL,
      seed: 401,
    }),
    roof: bakeShade(
      new THREE.SphereGeometry(0.18, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2).translate(0, 0.4, 0),
      { ...ROOF, seed: 402 },
    ),
  }),
  nordic: () => ({
    walls: bakeShade(new THREE.BoxGeometry(0.2, 0.6, 0.2).translate(0, 0.3, 0), {
      ...WALL,
      seed: 411,
    }),
    roof: merge([
      bakeShade(new THREE.ConeGeometry(0.19, 0.34, 4).rotateY(Math.PI / 4).translate(0, 0.77, 0), {
        ...ROOF,
        seed: 412,
      }),
      bakeShade(new THREE.ConeGeometry(0.02, 0.12, 4).translate(0, 1.0, 0), {
        bottom: 0.9,
        top: 1.1,
        jitter: 0.05,
        seed: 413,
      }),
    ]),
  }),
  eastern: () => ({
    walls: merge(
      [0, 1, 2].map((i) =>
        bakeShade(
          new THREE.BoxGeometry(0.2 - i * 0.05, 0.14, 0.2 - i * 0.05).translate(
            0,
            0.07 + i * 0.2,
            0,
          ),
          { ...WALL, seed: 421 + i },
        ),
      ),
    ),
    roof: merge(
      [0, 1, 2].map((i) =>
        bakeShade(frustum(0.2 - i * 0.05, 0.2 - i * 0.05, 0.14 + i * 0.2, 0.06, 0.45, 1.9), {
          ...ROOF,
          seed: 431 + i,
        }),
      ),
    ),
  }),
};

const CULTURE_PARTS = new Map<string, Record<Part, THREE.BufferGeometry>>();
function build(form: CultureForm): Record<Part, THREE.BufferGeometry> {
  const [base, culture] = form.split(':') as [HouseBase, CultureId];
  return base === 'keep'
    ? KEEPS[culture]()
    : culturalHouse(
        base,
        culture,
        500 + HOUSE_BASES.indexOf(base) * 10 + CULTURE_IDS.indexOf(culture),
      );
}

/** Geometry pair for any form: shared forms from `FORM_PARTS`, culture forms built once, lazily. */
export function formParts(form: BuildingForm): Record<Part, THREE.BufferGeometry> {
  if (!form.includes(':')) return FORM_PARTS[form as keyof typeof FORM_PARTS];
  let parts = CULTURE_PARTS.get(form);
  if (!parts) {
    parts = build(form as CultureForm);
    CULTURE_PARTS.set(form, parts);
  }
  return parts;
}

export function cultureOf(value: unknown): CultureId | undefined {
  return value === 'imperial' || value === 'nordic' || value === 'eastern' ? value : undefined;
}

/** Culture form for a shared house/keep form, or the form unchanged. */
export function cultureForm(form: BuildingForm, culture: CultureId | undefined): BuildingForm {
  return culture && (HOUSE_BASES as readonly string[]).includes(form)
    ? (`${form}:${culture}` as CultureForm)
    : form;
}

// --- Palettes (sRGB hex; weathered like the shared ones) ---

export const CULTURE_WALLS: Record<CultureId, readonly string[]> = {
  imperial: ['#e8e0cc', '#dfd6bd', '#efe8d6', '#d6cdb4'], // plaster / marble
  nordic: ['#7a5230', '#6e4a2c', '#86593a', '#64422a'], // dark oak
  eastern: ['#f0ece0', '#e6e0cf', '#ddd6c2', '#f4f0e6'], // white plaster
};
export const CULTURE_ROOFS: Record<CultureId, readonly string[]> = {
  imperial: ['#b5472a', '#c0522f', '#a63f25', '#cf6a45'], // terracotta
  nordic: ['#7a6048', '#6a523c', '#86694e', '#5e4833'], // shingle / turf
  eastern: ['#56625f', '#4a5654', '#626e6a', '#3f4a48'], // charcoal-green tile
};
